import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { TestTube, AlertTriangle, Eye, PlayCircle } from 'lucide-react';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

/**
 * MM audit 3.2: DRY_RUN mode toggle.
 *
 * Включает/выключает режим — все execute_buy/sell идут через
 * simulate (quote, но без BOC). Админ видит что бы MM СДЕЛАЛ,
 * не тратя реальные деньги. Persist в SQLite + видно в Prometheus
 * (mm_dry_run_active).
 */
const DryRunCard = () => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    try {
      const s = await fetch(`${API_URL}/api/market-maker/dry-run/status`).then(r => r.json());
      setStatus(s);
      const h = await fetch(`${API_URL}/api/market-maker/dry-run/history?limit=10`).then(r => r.json());
      setHistory(h.swaps || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const toggle = async () => {
    if (!status) return;
    const next = !status.active;
    const confirmMsg = next
      ? 'Включить DRY_RUN? Все swap-операции перестанут быть реальными.'
      : 'Выключить DRY_RUN? MM вернётся к реальной торговле.';
    if (!window.confirm(confirmMsg)) return;
    setToggling(true);
    try {
      const r = await fetch(`${API_URL}/api/market-maker/dry-run/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next, updated_by: 'ui' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`DRY_RUN ${next ? 'ENABLED' : 'DISABLED'}`);
      await load();
    } catch (e) {
      toast.error(`Toggle failed: ${e.message}`);
    } finally {
      setToggling(false);
    }
  };

  const active = !!status?.active;
  const envOverride = status?.env_override;

  return (
    <Card className={active ? 'border-yellow-500/50' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <TestTube className="h-4 w-4" /> DRY_RUN mode
            {active && (
              <Badge className="bg-yellow-600 animate-pulse text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                SIMULATED — no BOC sent
              </Badge>
            )}
            {!active && (
              <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-400/40">
                <PlayCircle className="h-3 w-3 mr-1" /> LIVE
              </Badge>
            )}
          </span>
          {envOverride && (
            <span className="text-xs text-slate-500">env override: {envOverride}</span>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          В DRY_RUN: execute_buy/sell делают full Omniston quote, но BOC не отправляется.
          Позиция не меняется. Полезно для тестирования изменений стратегии на prod данных.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <Button
            onClick={toggle}
            disabled={toggling || !!envOverride}
            variant={active ? 'destructive' : 'default'}
            className={active ? '' : 'bg-yellow-600 hover:bg-yellow-700'}
          >
            {toggling ? '...' : active ? 'Disable DRY_RUN' : 'Enable DRY_RUN'}
          </Button>
          {envOverride && (
            <span className="text-xs text-amber-400">
              Env MM_DRY_RUN={envOverride} блокирует UI toggle.
            </span>
          )}
          <div className="text-xs text-slate-400 ml-auto">
            <Eye className="h-3 w-3 inline mr-1" />
            Sim swaps log: {history.length > 0 ? `${history.length} recent` : 'empty'}
          </div>
        </div>

        {active && history.length > 0 && (
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200">
              Recent simulated swaps ({history.length})
            </summary>
            <table className="mt-2 w-full text-xs">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left">Time</th>
                  <th>Op</th>
                  <th className="text-right">USD</th>
                  <th className="text-right">Quote</th>
                  <th>Source</th>
                  <th>Tag</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="text-slate-400">{(h.timestamp || '').slice(0, 19)}</td>
                    <td className={h.operation_type === 'buy' ? 'text-emerald-400' : 'text-red-400'}>
                      {h.operation_type?.toUpperCase()}
                    </td>
                    <td className="text-right">${(h.amount_usd || 0).toFixed(2)}</td>
                    <td className="text-right">
                      {(h.quote_expected_out || 0).toFixed(4)}
                    </td>
                    <td className="text-xs">
                      {h.quote_error ? <span className="text-red-400">err</span> : h.quote_source}
                    </td>
                    <td className="text-slate-500 text-xs">{h.source_tag}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

export default DryRunCard;
